import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Calendar,
  Star,
  Settings,
  User,
  Search,
  LogOut,
  CheckCircle,
  X,
  Heart,
  Bell,
  HelpCircle,
} from 'lucide-react';
import LogoutModal from './LogoutModal';

const ClientSidebar = ({ isMobileOpen, onClose }) => {
  const { user, logout } = useAuth();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      setLoggingOut(false);
      setShowLogoutModal(false);
    }
  };

  const navigationGroups = [
    {
      label: 'Account',
      items: [
        { name: 'My account', href: '/dashboard', icon: User },
        { name: 'Settings', href: '/settings', icon: Settings },
      ],
    },
    {
      label: 'Bookings',
      items: [
        { name: 'My Bookings', href: '/bookings', icon: Calendar },
        { name: 'Favorites', href: '/favorites', icon: Heart },
        { name: 'My Reviews', href: '/reviews', icon: Star },
      ],
    },
    {
      label: 'Discover',
      items: [{ name: 'Browse Vendors', href: '/browse-vendors', icon: Search }],
    },
    {
      label: 'Support',
      items: [
        { name: 'Notifications', href: '/notifications', icon: Bell },
        { name: 'Help & Support', href: '/help', icon: HelpCircle },
      ],
    },
  ];

  return (
    <>
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 dashboard-surface bg-black/50 z-[11000]"
          onClick={onClose}
        />
      )}

      <div
        className={`w-full lg:w-64 flex-shrink-0 ${ isMobileOpen ? 'fixed inset-y-0 left-0 z-50 lg:relative lg:z-auto' : 'hidden lg:block' } transition-transform duration-300 ease-in-out`}
      >
        <div className="product-panel flex h-full flex-col overflow-hidden lg:sticky lg:top-24 lg:h-auto">
          {isMobileOpen && (
            <div className="flex items-center justify-between px-4 py-3 border-b border-border lg:hidden">
              <h2 className="text-sm font-semibold text-ink">Menu</h2>
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-product-md text-ink-tertiary hover:bg-surface-muted hover:text-ink transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}

          <div className="p-4 sm:p-5 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-product-md bg-ink">
                <span className="text-sm font-semibold text-white">
                  {user?.name ? user.name.charAt(0).toUpperCase() : 'C'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-ink truncate">
                  {user?.name || 'Client'}
                </p>
                <p className="text-xs text-ink-tertiary truncate">
                  {user?.email || ''}
                </p>
                <span className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-emerald-50 text-emerald-800">
                  <CheckCircle className="w-3 h-3" />
                  Active
                </span>
              </div>
            </div>
          </div>

          <nav className="p-3.5 space-y-5 flex-1 overflow-y-auto">
            {navigationGroups.map((group) => (
              <div key={group.label} className="space-y-1.5">
                <p className="px-2.5 mb-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-tertiary">
                  {group.label}
                </p>
                {group.items.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    onClick={() => {
                      if (isMobileOpen && onClose) onClose();
                    }}
                    className={({ isActive }) =>
                      `flex items-center px-2.5 py-3 text-sm font-medium rounded-product-md transition-colors ${
                        isActive
                          ? 'bg-ink text-white'
                          : 'text-ink-secondary hover:bg-surface-muted hover:text-ink'
                      }`
                    }
                  >
                    <item.icon className="w-5 h-5 mr-3 flex-shrink-0" />
                    <span>{item.name}</span>
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>

          <div className="p-3 border-t border-border">
            <button
              type="button"
              onClick={() => setShowLogoutModal(true)}
              className="flex items-center w-full px-2.5 py-2.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-product-md transition-colors"
            >
              <LogOut className="w-5 h-5 mr-3 flex-shrink-0" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => !loggingOut && setShowLogoutModal(false)}
        onConfirm={handleLogout}
        loading={loggingOut}
      />
    </>
  );
};

export default ClientSidebar;
